import React, { useEffect, useRef, useState, useCallback } from 'react'
import html2pdf from 'html2pdf.js'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import { getVesselImageUrl, getVesselParticulars } from '../core/_requests'
import type { Vessel, VesselParticulars } from '../core/_models'
import { getVesselDetailLabel } from '../core/vesselDetailMode'
import { VESSEL_PARTICULAR_SECTIONS } from '../core/vesselParticularSections'

interface Props {
  isOpen: boolean
  onClose: () => void
  vesselData: Vessel
}

const display = (value: unknown, unit?: string) => {
  if (value == null || value === '') return '—'
  return unit ? `${value} ${unit}` : String(value)
}

const ViewShipParticularsModal: React.FC<Props> = ({ isOpen, onClose, vesselData }) => {
  const printRef = useRef<HTMLDivElement>(null)
  const [data, setData] = useState<VesselParticulars>({ vesselId: vesselData.id })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)

  const fetchParticulars = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getVesselParticulars(vesselData.id)
      setData(response)
    } catch (err: any) {
      console.error('Failed to load vessel particulars:', err)
      const msg = showErrorToast(err, 'Failed to load vessel particulars.')
      setError(msg || 'Failed to load vessel particulars.')
      setData({ vesselId: vesselData.id })
    } finally {
      setLoading(false)
    }
    getVesselImageUrl(vesselData.id).then(setImageUrl).catch(() => setImageUrl(null))
  }, [vesselData.id])

  useEffect(() => {
    if (!isOpen) return
    fetchParticulars()
  }, [isOpen, fetchParticulars])

  const handlePrint = async () => {
    if (loading) {
      toast.warn('Please wait until vessel particulars finish loading.')
      return
    }
    const element = printRef.current
    if (!element) {
      toast.warn('Nothing available to export.')
      return
    }

    setIsDownloadingPdf(true)
    const vesselName = (data.vesselName || vesselData.fleet_name || 'Vessel').replace(/[^a-zA-Z0-9_-]/g, '_')
    try {
      await html2pdf()
        .set({
          margin: 0.4,
          filename: `VESSEL_PARTICULARS_${vesselName}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        })
        .from(element)
        .save()
      toast.success('Vessel Particulars PDF downloaded successfully.')
    } catch (err: any) {
      console.error('Failed to generate Vessel Particulars PDF:', err)
      showErrorToast(err, 'Failed to generate Vessel Particulars PDF.')
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className='modal-overlay' style={{ zIndex: 1050 }}>
      <div className='modal-content' style={{ maxWidth: '76rem', maxHeight: '92vh' }}>
        <div className='custom-modal-header d-flex justify-content-between align-items-center'>
          <h5 className='m-0'>View Vessel Particulars - {vesselData.fleet_name}</h5>
          <div className='d-flex gap-2'>
            <button
              className='btn btn-sm btn_primary d-inline-flex align-items-center gap-1'
              onClick={handlePrint}
              disabled={isDownloadingPdf}
            >
              {isDownloadingPdf ? (
                <>
                  <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                  <span>Generating PDF...</span>
                </>
              ) : (
                getVesselDetailLabel(vesselData.vesselType, 'print')
              )}
            </button>
            <button className='close-btn' onClick={onClose}>×</button>
          </div>
        </div>

        {error && (
          <div className='alert alert-warning d-flex justify-content-between align-items-center py-2 px-3 mx-3 mt-3 mb-0'>
            <span>{error}</span>
            <button
              type='button'
              className='btn btn-sm btn-outline-warning py-1 px-2'
              onClick={fetchParticulars}
              disabled={loading}
            >
              Retry
            </button>
          </div>
        )}

        <div className='custom-modal-body' style={{overflowY: 'auto', maxHeight: 'calc(92vh - 120px)'}}>
          {loading ? (
            <div className='text-muted py-5 text-center'>Loading...</div>
          ) : (
            <div ref={printRef} className='p-3'>
              <div className='d-flex align-items-start justify-content-between gap-4 mb-4'>
                <div>
                  <h4 className='mb-2'>{data.vesselName || vesselData.fleet_name}</h4>
                  <div className='text-muted'>Type: {display(data.vesselType || vesselData.vesselType)}</div>
                  <div className='text-muted'>IMO: {display(data.imoNumber || vesselData.imoNumber)}</div>
                </div>
                <div
                  className='border rounded bg-light d-flex align-items-center justify-content-center'
                  style={{width: 220, minHeight: 130, overflow: 'hidden'}}
                >
                  {imageUrl ? (
                    <img src={imageUrl} alt='Vessel' style={{width: '100%', objectFit: 'cover'}} />
                  ) : (
                    <span className='text-muted small'>No image</span>
                  )}
                </div>
              </div>

              {VESSEL_PARTICULAR_SECTIONS.map((section) => (
                <div className='mb-4' key={section.key}>
                  <h6 className='mb-3'>{section.label}</h6>
                  <table className='table table-sm table-bordered'>
                    <tbody>
                      {section.fields.map((field) => (
                        <tr key={String(field.key)}>
                          <th style={{width: '36%'}}>{field.label}</th>
                          <td>{display((data as any)[field.key], field.unit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ViewShipParticularsModal
